import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Loader2, Lock, Phone, Search, ShoppingCart, Star } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useMyMemberships } from '@/hooks/useMemberships';
import { PhoneDeskPanel } from '@/components/admin/PhoneDeskPanel';

/**
 * /admin/quick-order — the phone-order launchpad.
 *
 * Staff taking an order over the phone: find the customer, hit "Start
 * order", and the portal switches into that customer's context (RLS acts as
 * them — their pricing, their locations, their collection) landing directly
 * on the Shop. The order then flows through the normal cart → submit →
 * Service Fusion pipeline, indistinguishable from a customer-placed order
 * except for who submitted it (lifecycle emails go to the submitter AND the
 * customer's Primary email).
 */

interface EnabledCustomerRow {
  id: string;
  name: string;
  qbo_customer_id: number | null;
  on_credit_hold: boolean | null;
}

function useEnabledCustomers() {
  return useQuery<EnabledCustomerRow[]>({
    queryKey: ['admin', 'quick-order-customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('customers')
        .select('id, name, qbo_customer_id, on_credit_hold')
        .eq('active', true)
        .order('name');
      if (error) throw error;
      return (data ?? []) as EnabledCustomerRow[];
    },
    staleTime: 60 * 1000,
  });
}

export default function AdminQuickOrder() {
  const navigate = useNavigate();
  const { data: customers, isLoading, error } = useEnabledCustomers();
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  // Your ★ favorites sort to the top — the fastest path for the accounts
  // you take phone orders for daily.
  const { data: memberships } = useMyMemberships();
  const favoriteIds = useMemo(
    () => new Set((memberships ?? []).filter((m) => m.isFavorite).map((m) => m.customerId)),
    [memberships],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = customers ?? [];
    const matched = term
      ? all.filter(
          (c) =>
            c.name.toLowerCase().includes(term) ||
            String(c.qbo_customer_id ?? '').includes(term),
        )
      : all;
    return [...matched].sort((a, b) => {
      const fa = favoriteIds.has(a.id) ? 0 : 1;
      const fb = favoriteIds.has(b.id) ? 0 : 1;
      return fa - fb || a.name.localeCompare(b.name);
    });
  }, [customers, search, favoriteIds]);

  // Open the Shop as this customer in a NEW TAB (per-tab acting context,
  // docs/ACTING-CONTEXT.md) — this admin tab stays put, and the order tab
  // carries its own acting-customer header.
  function startOrder(c: EnabledCustomerRow) {
    setNotice(`Opening ${c.name} in a new tab — anything you order there is for ${c.name}.`);
    // The notice describes something that JUST happened in another tab, so it
    // has to expire. Left standing it reads as a statement about the current
    // session and claims you are still in that account long after you left it.
    window.setTimeout(() => setNotice(null), 8000);
    window.open(`/order?act_as=${encodeURIComponent(c.id)}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="px-6 py-6 space-y-5 max-w-3xl">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-brix-primary-tint text-brix-primary flex items-center justify-center shrink-0">
          <Phone size={18} />
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold text-brix-ink">Phone orders</h2>
          <p className="text-sm text-brix-muted mt-0.5 leading-relaxed">
            Taking an order over the phone? Find the customer and hit{' '}
            <span className="font-semibold text-brix-ink">Start order</span> — you&rsquo;ll be
            switched into their account with their pricing and locations, landing on the
            Shop. Submit works exactly like it does for them; the confirmation email goes
            to you and their primary email.
          </p>
        </div>
      </div>

      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted" />
        <Input
          autoFocus
          placeholder="Search customers by name or QBO #…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {notice && (
        <div className="rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
          {notice}
        </div>
      )}

      <div className="rounded-xl border border-brix-border bg-brix-surface overflow-hidden">
        {isLoading ? (
          <div className="px-4 py-10 text-center text-sm text-brix-muted">
            <Loader2 size={16} className="animate-spin inline-block mr-2" />
            Loading customers…
          </div>
        ) : error ? (
          <div className="px-4 py-10 text-center text-sm text-brix-warning">
            Could not load customers — refresh to retry.
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-brix-muted">
            {search ? 'No customers match that search.' : 'No enabled customers yet.'}
          </div>
        ) : (
          <ul className="divide-y divide-brix-border">
            {filtered.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-4 py-3">
                {favoriteIds.has(c.id) ? (
                  <Star size={15} className="text-brix-accent fill-brix-accent shrink-0" />
                ) : (
                  <Building2 size={15} className="text-brix-primary shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-brix-ink truncate">
                    {c.name}
                    {c.on_credit_hold && (
                      <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                        <Lock size={9} /> Ordering locked
                      </span>
                    )}
                  </p>
                  {c.qbo_customer_id != null && (
                    <p className="text-[11px] font-mono text-brix-muted">QBO #{c.qbo_customer_id}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/customers/${c.id}`)}
                  className="text-xs text-brix-muted hover:text-brix-ink underline cursor-pointer shrink-0"
                >
                  Manage
                </button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => startOrder(c)}
                  className="shrink-0"
                >
                  <ShoppingCart size={13} />
                  Start order
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* The live receptionists' page (/desk) and who may use it. */}
      <div className="flex items-center justify-between gap-3 rounded-xl border border-brix-border bg-brix-surface px-4 py-3">
        <p className="text-sm text-brix-muted">
          The <span className="font-semibold text-brix-ink">phone desk</span> is the narrow page the
          live receptionists use: look up a caller, see their orders, place an order, open a service request.
        </p>
        <Button type="button" size="sm" variant="secondary" onClick={() => navigate('/desk')} className="shrink-0">
          <Phone size={13} aria-hidden /> Open /desk
        </Button>
      </div>
      <PhoneDeskPanel />
    </div>
  );
}
